import {
  ConditionDTO,
  ModuleDTO,
  PatientDTO,
  SymptomDTO,
} from "@/app/api/patient/types";
import {
  IAdditionalMedicalData,
  ICondition,
  IModule,
  IPatientList,
} from "@/app/mock-data/getPatientsList";
import { EGender } from "@/app/types/enumerations";
import { ELocale } from "@/app/types/locale";
import { ELocalStorageKeys } from "@/app/types/localStorage";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const setItemLocalStorage = (item: string, value: string) => {
  return localStorage.setItem(item, value);
};

export const getItemLocaleStorage = (item: ELocalStorageKeys) => {
  return localStorage.getItem(item);
};

export const removeItemLocalStorage = (item: ELocalStorageKeys) => {
  return localStorage.removeItem(item);
};

export const setItemSessionStorage = (
  item: ELocalStorageKeys,
  value: string
) => {
  return sessionStorage.setItem(item, value);
};

export const getItemSessionStorage = (item: ELocalStorageKeys) => {
  return sessionStorage.getItem(item);
};

export const parseBirthdate = (
  birthdateInput: string | Date
): { year: string; month: string; day: string } => {
  const birthdate = new Date(birthdateInput);

  if (isNaN(birthdate.getTime())) {
    return {
      year: "0000",
      month: "00",
      day: "00",
    };
  }

  const year = String(birthdate.getFullYear());
  const month = String(birthdate.getMonth() + 1).padStart(2, "0");
  const day = String(birthdate.getDate()).padStart(2, "0");

  return { year, month, day };
};

export const getLocalizedBirthdate = (
  birthdateInput: string | Date,
  locale: string
): string => {
  const date = new Date(birthdateInput);

  return date.toLocaleDateString(locale, {
    timeZone: "UTC",
  });
};

export const buildBirthdateString = (
  year: string,
  month: string,
  day: string
): string => {
  const paddedMonth = month.padStart(2, "0");
  const paddedDay = day.padStart(2, "0");

  return `${year}-${paddedMonth}-${paddedDay}`;
};

export const unescapeHtml = (htmlString: string) => {
  const doc = new DOMParser().parseFromString(htmlString, "text/html");
  return doc.documentElement.textContent || "";
};

type ConnectedPatient = PatientDTO & {
  _id: string;
  disease: {
    module: {
      _id: string;
      name: IModule["name"];
      conditions?: string[];
    };
    condition: {
      _id: ICondition["id"];
      name: ICondition["name"];
      symptoms?: string[];
    };
  };
  country?: {
    name?: string;
  };
  personalData?: {
    identification?: {
      number?: string;
    };
  };
  additional?: IAdditionalMedicalData;
};

interface PatientConnection {
  patient?: ConnectedPatient | PatientDTO | string;
}

export const formatPatientsListFromConnections = (
  connectionList: PatientConnection[],
): IPatientList[] => {
  return connectionList
    .filter((entry) => entry.patient)
    .map((entry) => {
      const patient = entry.patient as ConnectedPatient;

      const disease: ICondition = {
        id: patient.disease.condition._id,
        name: patient.disease.condition.name,
        symptoms: patient.disease.condition.symptoms ?? [],
      };

      const module: IModule = {
        id: patient.disease.module._id,
        name: patient.disease.module.name,
        conditions: patient.disease.module.conditions ?? [],
      };

      const additional: IAdditionalMedicalData = {
        allergies: patient.additional?.allergies,
        others: patient.additional?.others,
        surgeries: patient.additional?.surgeries,
      };

      return {
        id: patient._id,
        auth: patient.auth,
        email: patient.email,
        firstname: patient.firstname,
        surnames: patient.surnames,
        birthdate: patient.birthdate,
        phoneNumber: patient.phoneNumber,
        height: patient.height,
        weight: patient.weight,
        gender: patient.gender as EGender,
        language: patient.language as ELocale,
        country: patient.country?.name ?? "N/A",
        dni: patient.personalData?.identification?.number ?? "N/A",
        disease,
        module,
        additional,
      };
    });
};

export const formatPatientDTO = (patient: PatientDTO): IPatientList => {
  const condition = patient.disease?.condition as ConditionDTO;
  const module = patient.disease?.module as ModuleDTO;

  const formattedCondition: ICondition = {
    id: condition?.id || "UNKNOWN",
    name: condition?.name || "UNKNOWN",
    symptoms:
      condition?.symptoms?.map((s: SymptomDTO | string) =>
        typeof s === "string" ? s : s.name
      ) || [],
  };

  const formattedModule: IModule = {
    id: module?.id || "digestive",
    name: module?.name || "digestive",
    conditions:
      module?.conditions?.map((c: ConditionDTO | string) =>
        typeof c === "string" ? c : c.name
      ) || [],
  };

  const additional: IAdditionalMedicalData = {
    allergies: undefined,
    others: undefined,
    surgeries: undefined,
  };

  return {
    id: patient.id,
    auth: typeof patient.auth === "string" ? patient.auth : patient.auth.id,
    email: patient.email,
    firstname: patient.firstname,
    surnames: patient.surnames,
    birthdate: patient.birthdate.toString(),
    phoneNumber: patient.phoneNumber,
    height: patient.height,
    weight: patient.weight,
    gender: patient.gender as EGender,
    language: patient.language as ELocale,
    country: "Spain",
    dni: "N/A",
    disease: formattedCondition,
    module: formattedModule,
    additional,
  };
};

export const capitalize = (str: string) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};
